import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Linkedin, Twitter, Mail, User } from 'lucide-react';
import { useTeam } from '../hooks/useAPI';

const TeamSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const { data: teamMembers, isLoading, error } = useTeam();

  // Default team members if API fails
  const defaultTeam = [
    {
      id: 1,
      name: 'Alex Thompson',
      role: 'CEO & Founder',
      bio: 'Visionary leader with 15+ years in technology innovation and business strategy.',
      image: null,
      linkedin: 'https://linkedin.com/in/alexthompson',
      twitter: 'https://twitter.com/alexthompson',
      email: 'alex@liontek.com',
      skills: ['Leadership', 'Strategy', 'Innovation']
    },
    {
      id: 2,
      name: 'Sarah Chen',
      role: 'CTO',
      bio: 'Technical expert specializing in AI/ML and scalable system architecture.',
      image: null,
      linkedin: 'https://linkedin.com/in/sarahchen',
      twitter: null,
      email: 'sarah@liontek.com',
      skills: ['AI/ML', 'System Architecture', 'Cloud Computing']
    },
    {
      id: 3,
      name: 'Michael Rodriguez',
      role: 'Lead Developer',
      bio: 'Full-stack developer passionate about creating elegant and efficient solutions.',
      image: null,
      linkedin: 'https://linkedin.com/in/michaelrodriguez',
      twitter: 'https://twitter.com/mrodriguez',
      email: 'michael@liontek.com',
      skills: ['Full-Stack Development', 'React', 'Node.js']
    },
    {
      id: 4,
      name: 'Emily Johnson',
      role: 'UX/UI Designer',
      bio: 'Creative designer focused on user-centered design and digital experiences.',
      image: null,
      linkedin: 'https://linkedin.com/in/emilyjohnson',
      twitter: null,
      email: 'emily@liontek.com',
      skills: ['UX/UI Design', 'User Research', 'Prototyping']
    },
    {
      id: 5,
      name: 'David Park',
      role: 'DevOps Engineer',
      bio: 'Infrastructure specialist ensuring reliable and scalable deployment solutions.',
      image: null,
      linkedin: 'https://linkedin.com/in/davidpark',
      twitter: 'https://twitter.com/davidpark',
      email: 'david@liontek.com',
      skills: ['DevOps', 'Kubernetes', 'AWS']
    },
    {
      id: 6,
      name: 'Lisa Wang',
      role: 'Data Scientist',
      bio: 'Analytics expert turning data into actionable insights and intelligent solutions.',
      image: null,
      linkedin: 'https://linkedin.com/in/lisawang',
      twitter: null,
      email: 'lisa@liontek.com',
      skills: ['Data Science', 'Machine Learning', 'Analytics']
    }
  ];

  const displayTeam = teamMembers && teamMembers.length > 0 ? teamMembers : defaultTeam;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.5, ease: 'easeOut' }
    }
  };

  return (
    <section id="team" className="py-20 md:py-24 bg-lion-gray">
      <div className="container mx-auto px-6" ref={ref}>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="text-center mb-16"
        >
          <motion.span 
            variants={itemVariants}
            className="font-bold text-lion-yellow text-sm uppercase tracking-wider"
          >
            OUR TEAM
          </motion.span>
          
          <motion.h2 
            variants={itemVariants}
            className="text-3xl md:text-4xl font-poppins font-bold text-lion-blue mt-2 mb-4"
          >
            Meet the Experts Behind LionTek
          </motion.h2>
          
          <motion.p 
            variants={itemVariants}
            className="max-w-2xl mx-auto text-gray-600"
          >
            Our diverse team of passionate professionals brings together years of experience and cutting-edge expertise to deliver exceptional results.
          </motion.p>
        </motion.div>

        {isLoading ? (
          <div className="flex justify-center">
            <div className="spinner w-8 h-8"></div>
          </div>
        ) : (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {displayTeam.map((member, index) => (
              <motion.div
                key={member.id || index}
                variants={cardVariants}
                custom={index}
                className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 group"
              >
                {/* Member Photo */}
                <div className="relative h-64 bg-gradient-to-br from-lion-blue to-lion-yellow overflow-hidden">
                  {member.image ? (
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <User className="w-24 h-24 text-white opacity-70" />
                    </div>
                  )}
                  
                  {/* Social Media Overlay */}
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-70 transition-all duration-300 flex items-center justify-center">
                    <div className="flex space-x-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      {member.linkedin && (
                        <a
                          href={member.linkedin}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-10 h-10 bg-white rounded-full flex items-center justify-center text-lion-blue hover:bg-lion-yellow hover:text-white transition-colors duration-300"
                        >
                          <Linkedin className="w-5 h-5" />
                        </a>
                      )}
                      
                      {member.twitter && (
                        <a
                          href={member.twitter}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-10 h-10 bg-white rounded-full flex items-center justify-center text-lion-blue hover:bg-lion-yellow hover:text-white transition-colors duration-300"
                        >
                          <Twitter className="w-5 h-5" />
                        </a>
                      )}
                      
                      {member.email && (
                        <a
                          href={`mailto:${member.email}`}
                          className="w-10 h-10 bg-white rounded-full flex items-center justify-center text-lion-blue hover:bg-lion-yellow hover:text-white transition-colors duration-300"
                        >
                          <Mail className="w-5 h-5" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="p-6">
                  <h3 className="text-xl font-poppins font-bold text-lion-blue mb-1 group-hover:text-lion-yellow transition-colors duration-300">
                    {member.name}
                  </h3>
                  
                  <p className="text-lion-yellow font-medium mb-3">
                    {member.position}
                  </p>
                  
                  <p className="text-gray-600 text-sm leading-relaxed mb-4">
                    {member.bio}
                  </p>
                  
                  {/* Skills */}
                  {member.skills && member.skills.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="text-sm font-semibold text-lion-blue">Expertise:</h4>
                      <div className="flex flex-wrap gap-2">
                        {member.skills.slice(0, 3).map((skill, idx) => (
                          <span
                            key={idx}
                            className="bg-lion-gray text-gray-600 text-xs px-2 py-1 rounded"
                          >
                            {skill}
                          </span>
                        ))}
                        {member.skills.length > 3 && (
                          <span className="text-xs text-gray-500">
                            +{member.skills.length - 3} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}

        {error && (
          <motion.p 
            variants={itemVariants}
            className="text-gray-500 text-center mt-8"
          >
            Team information will be loaded shortly. Please check back soon.
          </motion.p>
        )}

        {/* Join Our Team CTA */}
        <motion.div
          variants={itemVariants}
          className="text-center mt-16 bg-white p-8 rounded-lg"
        >
          <h3 className="text-2xl font-poppins font-bold text-lion-blue mb-4">
            Join Our Growing Team
          </h3>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            We're always looking for talented individuals who share our passion for innovation and excellence. 
            Explore career opportunities at LionTek.
          </p>
          <a
            href="#contact"
            className="inline-flex items-center bg-lion-yellow text-lion-blue px-8 py-3 rounded-lg font-semibold hover:bg-yellow-400 transform hover:scale-105 transition-all duration-300"
          >
            View Open Positions
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default TeamSection;